import { Link } from "react-router-dom";
import { Heart, UtensilsCrossed, Leaf, Clock, ArrowRight, CheckCircle2, Coffee, ShoppingBag, Store, Truck, TrendingUp, Award, Globe, Users } from "lucide-react";
import { BRANDS, IMAGES, PRODUCTS, STATS, TESTIMONIALS, LOGOS, BRANDS_PAGE_LOGOS } from "@/data/site";
import BrandCard from "@/components/BrandCard";
import Reveal from "@/components/Reveal";

const PHILOSOPHY = [
  { icon: Heart, title: "Taste First", text: "Millet food that's crafted to be crave-worthy. Great taste. Always.", image: IMAGES.promillBowl },
  { icon: UtensilsCrossed, title: "Familiar Indian Formats", text: "From dosas to parathas, pulas to snacks — the dishes you love, reimagined with millets.", image: IMAGES.milletPlazaDining },
  { icon: Leaf, title: "Clean Modern Ingredients", text: "Whole millets. Honest ingredients. Nothing unnecessary.", image: IMAGES.milletGrains },
  { icon: Clock, title: "Everyday Convenience", text: "Ready in minutes. Easy to cook, carry and enjoy — every day.", image: IMAGES.packaged },
];

const ECOSYSTEM_FORMATS = [
  { icon: Store, title: "Dine-In", sub: "Restaurant Experiences" },
  { icon: ShoppingBag, title: "On-the-Go", sub: "Kiosks & Mobile Food" },
  { icon: Coffee, title: "Café", sub: "Conversations & Comfort" },
  { icon: Leaf, title: "Packaged", sub: "Goodness You Can Take" },
  { icon: Truck, title: "Retail", sub: "Closer to You" },
];

const STAT_VISUAL = [
  { icon: UtensilsCrossed, hint: "served & smiled" },
  { icon: Award, hint: "in our pantry" },
  { icon: Globe, hint: "and counting" },
  { icon: TrendingUp, hint: "of expertise" },
];

export default function Home() {
  return (
    <div data-testid="page-home" className="bg-[var(--bg)]">
      {/* ─────────────────── HERO ─────────────────── */}
      <section className="pt-[110px] md:pt-[120px] pb-12 md:pb-16 bg-[var(--bg)] relative overflow-hidden">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left — editorial headline */}
          <Reveal className="lg:col-span-6">
            <div className="overline">The Native Touch Philosophy</div>

            <h1 className="font-serif-display text-[2.75rem] sm:text-[3.5rem] md:text-[4.5rem] lg:text-[5.25rem] leading-[1.02] text-[var(--ink)] mt-6">
              More Than Food.<br />
              <span className="text-[var(--green)] italic">A Native Way of Life.</span>
            </h1>

            <div className="h-[3px] w-20 bg-[var(--gold)] mt-7 rounded-full" />

            <p className="mt-7 max-w-xl text-[var(--ink-2)] text-base md:text-lg leading-relaxed">
              Celebrating India's rich culinary heritage through thoughtfully crafted products,
              authentic experiences, and nourishing choices for modern families.
            </p>

            {/* Pull-quote callout */}
            <div className="mt-9 inline-flex items-center gap-4 bg-white border border-[var(--line-soft)] rounded-full pl-3 pr-6 py-3 shadow-sm max-w-xl">
              <div className="w-10 h-10 rounded-full bg-[var(--bg-cream)] flex items-center justify-center shrink-0">
                <Leaf size={18} className="text-[var(--gold)]" />
              </div>
              <p className="font-serif-display italic text-[var(--green)] text-[0.95rem] md:text-base leading-snug">
                We turn conscious eating into <strong className="not-italic font-semibold">something people actually crave.</strong>
              </p>
            </div>

            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/products" data-testid="hero-cta-shop" className="btn-primary">
                Shop Products <ArrowRight size={14} />
              </Link>
              <Link to="/brands" data-testid="hero-cta-brands" className="btn-outline">
                Explore Brands <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>

          {/* Right — editorial image stack */}
          <Reveal delay={0.15} className="lg:col-span-6 relative">
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-7 aspect-[3/4] overflow-hidden rounded-2xl bg-[var(--bg-soft)]">
                <img src={IMAGES.promillBowl} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-5 flex flex-col gap-3">
                <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-[var(--bg-soft)]">
                  <img src={IMAGES.milletPlazaDining} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-[var(--bg-soft)]">
                  <img src={IMAGES.packaged} alt="" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>

            <div className="absolute -bottom-5 left-5 md:left-8 bg-white border border-[var(--line)] rounded-xl px-4 py-3 flex items-center gap-3 shadow-lg hidden md:flex">
              <div className="w-10 h-10 rounded-full bg-[var(--bg-cream)] flex items-center justify-center">
                <CheckCircle2 size={20} className="text-[var(--green)]" />
              </div>
              <div>
                <div className="font-serif-display text-base font-semibold text-[var(--ink)] leading-tight">5,000+ retail touchpoints</div>
                <div className="text-xs text-[var(--ink-3)]">across India</div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* ── Hero brand strip ── */}
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 mt-16">
          <Reveal>
            <div className="bg-white border border-[var(--line-soft)] rounded-2xl px-5 md:px-8 py-5 md:py-6">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-center">
                <div className="md:col-span-3 flex items-center gap-3 md:border-r md:border-[var(--line-soft)] md:pr-6">
                  <div className="w-2 h-12 bg-[var(--gold)] rounded-full" />
                  <div>
                    <div className="kbd text-[var(--green)]">Part of the Family</div>
                    <div className="font-serif-display text-lg font-semibold text-[var(--ink)] leading-tight">Four brands.<br />One ecosystem.</div>
                  </div>
                </div>
                <div className="md:col-span-9 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
                  {BRANDS.map((b) => (
                    <Link
                      key={b.slug}
                      to={`/brands/${b.slug}`}
                      data-testid={`hero-brand-${b.slug}`}
                      className="group flex items-center gap-3 p-3 rounded-xl hover:bg-[var(--bg-cream)] transition border border-transparent hover:border-[var(--line-soft)]"
                    >
                      <div className="w-14 h-14 rounded-lg bg-[var(--bg-cream)] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                        <img src={b.logo} alt={b.name} className="h-10 w-auto object-contain" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-serif-display text-sm font-semibold text-[var(--ink)] truncate">{b.name.split("—")[0].trim()}</div>
                        <div className="kbd text-[0.6rem] text-[var(--ink-3)] truncate">{b.category.split("·")[0].trim()}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────── PHILOSOPHY ───────────── */}
      <section className="bg-[var(--bg-soft)] relative overflow-hidden pt-16 md:pt-20 pb-10 md:pb-12">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-4">
            <div className="overline">The Native Touch Philosophy</div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight text-[var(--ink)] mt-5 font-semibold">
              Not a Compromise. <br />
              <span className="text-[var(--green)]">A Better Way to Eat.</span>
            </h2>
            <div className="h-[3px] w-20 bg-[var(--gold)] mt-7 rounded-full" />
            <p className="mt-7 text-[var(--ink-2)] leading-relaxed max-w-md">
              We make millets premium, delicious, familiar and contemporary — <strong className="text-[var(--green)]">not</strong> poor-man food, <strong className="text-[var(--green)]">not</strong> boring health food, <strong className="text-[var(--green)]">not</strong> niche diet food.
            </p>
            <div className="mt-8 bg-white border border-[var(--line-soft)] rounded-2xl p-5 flex items-center gap-4">
              <div className="w-12 h-12 deco-ear shrink-0" />
              <p className="font-serif-display text-[var(--green)] italic leading-snug">
                We turn conscious eating into <strong className="not-italic font-semibold">something people actually crave.</strong>
              </p>
            </div>
          </Reveal>

          <div className="lg:col-span-8 grid grid-cols-2 lg:grid-cols-4 gap-5">
            {PHILOSOPHY.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="card-soft h-full overflow-hidden flex flex-col">
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-5">
                    <div className="w-11 h-11 rounded-full bg-[var(--green)] flex items-center justify-center -mt-12 mb-3 border-4 border-white shadow-sm">
                      <p.icon size={18} className="text-white" />
                    </div>
                    <h3 className="font-serif-display text-xl md:text-[1.35rem] font-semibold text-[var(--ink)] leading-tight">{p.title}</h3>
                    <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">{p.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Food-first banner */}
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 mt-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 overflow-hidden rounded-2xl bg-white border border-[var(--line-soft)]">
            <div className="lg:col-span-5 aspect-[4/3] lg:aspect-auto overflow-hidden">
              <img src={IMAGES.familyDining} alt="Family enjoying meal" className="w-full h-full object-cover" />
            </div>
            <div className="lg:col-span-7 p-8 md:p-10 flex flex-col justify-center">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 deco-ear" />
                <h3 className="font-serif-display text-3xl md:text-4xl text-[var(--green)] font-semibold leading-tight">
                  Food-first. Consumer-first. <br />Design-led.
                </h3>
              </div>
              <p className="mt-6 text-[var(--ink-2)] leading-relaxed max-w-2xl">
                Everything we create starts with the consumer and ends with delight. We blend
                nutrition, taste and design to make millets the new normal.
              </p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  "Thoughtful recipes people love",
                  "Premium ingredients you can trust",
                  "Beautifully designed experiences",
                  "Building brands that last",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-sm text-[var(--ink-2)]">
                    <CheckCircle2 size={18} className="text-[var(--gold)] shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── ORBITAL moved to About page ───────────── */}

      {/* ───────────── DECORATIVE DIVIDER — A Native Touch ───────────── */}
      <section className="relative bg-[#0b3a20] py-14 md:py-20 overflow-hidden">
        <img
          src={IMAGES.milletBranch}
          alt=""
          aria-hidden="true"
          className="absolute left-0 top-0 h-[140%] -translate-y-[10%] -translate-x-[10%] opacity-90 pointer-events-none select-none"
        />
        <img
          src={IMAGES.milletBranch}
          alt=""
          aria-hidden="true"
          className="absolute right-0 top-0 h-[140%] -translate-y-[10%] translate-x-[10%] opacity-90 pointer-events-none select-none"
          style={{ transform: "scaleX(-1) translateY(-10%) translateX(-10%)" }}
        />
        <div className="relative max-w-2xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="h-[1px] w-10 bg-[var(--gold)]/70" />
            <span className="overline text-[var(--gold-soft)]">A Native Touch</span>
            <span className="h-[1px] w-10 bg-[var(--gold)]/70" />
          </div>
          <p className="font-serif-display italic text-2xl md:text-4xl text-white leading-snug">
            Where <span className="text-[var(--gold)] not-italic font-semibold">ancient grains</span> meet modern life.
          </p>
          <p className="text-white/75 mt-5 text-sm md:text-base max-w-xl mx-auto">
            Rooted in the fields of India. Reimagined for the families of today.
          </p>
        </div>
      </section>

      {/* ───────────── FOUR BRANDS GRID ───────────── */}
      <section className="relative bg-[var(--bg-soft)] pt-16 md:pt-20 pb-20 md:pb-24 overflow-hidden">
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <div className="overline overline-green">Our Ecosystem</div>
              <h2 className="font-serif-display text-4xl md:text-6xl mt-5 leading-[1.05] text-[var(--ink)] font-semibold">
                Four Brands. <span className="text-[var(--gold)]">One Food Ecosystem.</span>
              </h2>
              <div className="flex items-center gap-3 justify-center mt-6">
                <span className="h-[1px] w-12 bg-[var(--gold)]/60" />
                <p className="text-sm text-[var(--ink-2)]">One platform. Multiple formats. One premium millet movement.</p>
                <span className="h-[1px] w-12 bg-[var(--gold)]/60" />
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mt-12">
            {BRANDS.map((b, i) => (
              <Reveal key={b.slug} delay={i * 0.08}>
                <BrandCard brand={b} logoOverride={BRANDS_PAGE_LOGOS[b.slug]} premium />
              </Reveal>
            ))}
          </div>

          {/* One Ecosystem. Every Occasion. */}
          <Reveal>
            <div className="mt-14 bg-white border border-[var(--line-soft)] rounded-2xl px-6 md:px-10 py-8 md:py-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-3 border-l-4 border-[var(--gold)] pl-4">
                  <h3 className="font-serif-display text-2xl md:text-3xl font-semibold text-[var(--green)] leading-tight">
                    One Ecosystem. <br />Every Occasion.
                  </h3>
                </div>
                <div className="lg:col-span-9 grid grid-cols-2 md:grid-cols-5 gap-3">
                  {ECOSYSTEM_FORMATS.map((f, i) => (
                    <div key={f.title} className="flex items-center gap-3 relative">
                      <div className="w-14 h-14 rounded-full bg-[var(--bg-cream)] border border-[var(--line-soft)] flex items-center justify-center shrink-0">
                        <f.icon size={22} className="text-[var(--green)]" />
                      </div>
                      <div>
                        <div className="kbd text-[var(--green)]">{f.title}</div>
                        <div className="text-xs text-[var(--ink-3)] mt-1">{f.sub}</div>
                      </div>
                      {i < ECOSYSTEM_FORMATS.length - 1 && (
                        <span className="hidden md:block absolute right-[-10px] top-1/2 -translate-y-1/2 text-[var(--gold)]/60">····</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────────── FEATURED PRODUCTS ───────────── */}
      <section className="section bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <Reveal>
              <div className="overline overline-green">Featured Products</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold">
                Pantry essentials, <span className="text-[var(--green)]">reimagined.</span>
              </h2>
            </Reveal>
            <Link to="/products" data-testid="home-products-cta" className="btn-outline">
              Shop all products <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
            {PRODUCTS.slice(0, 5).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05}>
                <Link to="/products" data-testid={`home-product-${p.id}`} className="card-soft block overflow-hidden">
                  <div className="aspect-square bg-[var(--bg)] overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif-display text-base md:text-lg font-semibold text-[var(--ink)] leading-tight line-clamp-2">{p.name}</h3>
                    <div className="text-xs text-[var(--ink-3)] mt-1">{p.weight}</div>
                    <div className="mt-3 flex items-center justify-between">
                      <span className="font-serif-display text-lg font-semibold text-[var(--green)]">{p.price}</span>
                      <span className="text-[10px] uppercase tracking-widest font-semibold text-[var(--green)]">View →</span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── BRAND VIDEO ───────────── */}
      <section className="section bg-[var(--bg-soft)] relative overflow-hidden">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* LEFT — Editorial copy */}
            <Reveal className="lg:col-span-5 order-2 lg:order-1">
              <div className="overline overline-green">The Native Touch Film</div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl mt-5 leading-[1.05] text-[var(--ink)] font-semibold">
                A movement, <span className="text-[var(--green)] italic">in motion.</span>
              </h2>
              <div className="h-[3px] w-16 bg-[var(--gold)] mt-7 rounded-full" />

              <p className="mt-7 text-[var(--ink-2)] text-base md:text-lg leading-relaxed max-w-lg">
                From a single grain of millet to four homegrown brands — watch the people,
                the kitchens and the families behind <span className="text-[var(--green)] font-semibold">Native Touch</span>.
                A short film about reclaiming what we eat, the modern Indian way.
              </p>

              <ul className="mt-7 space-y-3.5 text-sm text-[var(--ink-2)]">
                {[
                  { k: "Featuring", v: "Ashish Vidyarthi" },
                  { k: "Length", v: "Brand Story · ~2 min" },
                  { k: "Origin", v: "Native Touch Group, India" },
                ].map((row) => (
                  <li key={row.k} className="flex items-start gap-3">
                    <CheckCircle2 size={16} className="text-[var(--gold)] mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-[var(--green)] font-semibold uppercase tracking-widest text-[0.65rem]">{row.k}</strong>
                      <span className="ml-2 text-[var(--ink)]">{row.v}</span>
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/franchise" data-testid="video-cta-partner" className="btn-primary">
                  Partner with us <ArrowRight size={14} />
                </Link>
                <Link to="/about" className="btn-outline">Read the story</Link>
              </div>
            </Reveal>

            {/* RIGHT — Video player */}
            <Reveal delay={0.1} className="lg:col-span-7 order-1 lg:order-2">
              <div className="relative pt-3 pr-3">
                {/* Decorative offset frame — sits behind the video only, not the caption */}
                <div className="absolute top-0 right-0 left-3 bottom-3 rounded-2xl border-2 border-[var(--gold)]/40 pointer-events-none hidden md:block" />

                <div className="relative rounded-2xl overflow-hidden bg-[var(--green-dark)] border border-[var(--line-soft)] shadow-[0_30px_80px_-30px_rgba(11,58,32,0.45)]">
                  {/* Gold corner accents */}
                  <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-[var(--gold)]/70 rounded-tl-2xl pointer-events-none z-10" />
                  <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-[var(--gold)]/70 rounded-br-2xl pointer-events-none z-10" />

                  <div className="relative w-full aspect-video">
                    <iframe
                      data-testid="brand-video"
                      src="https://www.youtube-nocookie.com/embed/xSx_t5Fm6NY?rel=0&modestbranding=1&playsinline=1"
                      title="Native Touch Group — Brand Film"
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    />
                  </div>
                </div>
              </div>

              {/* Caption pill — outside the offset-frame wrapper so the border never clips it */}
              <div className="mt-5 inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.22em] font-semibold text-[var(--ink-3)]">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]" />
                Press play · A film by Native Touch
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────── FRANCHISE STRIP ───────────── */}
      <section className="relative overflow-hidden">
        <img src={IMAGES.franchiseKitchen} alt="Chefs preparing millet meals in a professional kitchen" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 28%" }} />
        {/* Tight left-anchored gradient: very dark only behind the text column, then quickly fades so both chefs remain visible */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to right, rgba(11,58,32,0.94) 0%, rgba(11,58,32,0.88) 22%, rgba(11,58,32,0.55) 38%, rgba(11,58,32,0.30) 60%, rgba(11,58,32,0.20) 100%)",
          }}
        />
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-10 py-20 md:py-28 text-white">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-7">
              <div className="overline text-[var(--gold-soft)]">Franchise & Partner Gateway</div>
              <h2 className="font-serif-display text-4xl md:text-6xl mt-5 leading-tight font-semibold drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]">
                Build the Native Touch network <span className="text-[var(--gold)]">in your city.</span>
              </h2>
              <p className="mt-6 text-white/90 max-w-xl leading-relaxed drop-shadow-[0_1px_6px_rgba(0,0,0,0.5)]">
                Multi-format pathways from a single GoMill kiosk to a full Millet Plaza — with operational,
                supply and marketing support from day one.
              </p>
            </div>
            <div className="md:col-span-5 flex flex-wrap gap-3 md:justify-end">
              <Link to="/franchise" data-testid="franchise-strip-cta" className="btn-gold">
                Explore Partnership <ArrowRight size={14} />
              </Link>
              <Link to="/franchise" data-testid="franchise-strip-brochure" className="btn-ghost-light border border-white/40">
                Download Brochure
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── TESTIMONIALS ───────────── */}
      <section className="section bg-[var(--bg-soft)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <div className="overline overline-green">What People Say</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold">
                Loved by those who eat <span className="text-[var(--green)]">with intention.</span>
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.06}>
                <div className="card-soft p-7 h-full">
                  <div className="font-serif-display text-5xl text-[var(--gold)] leading-none">"</div>
                  <p className="font-serif-display text-lg md:text-xl text-[var(--ink)] leading-snug mt-2">{t.quote}</p>
                  <div className="mt-7 pt-5 border-t border-[var(--line-soft)]">
                    <div className="text-sm font-semibold text-[var(--green)]">{t.name}</div>
                    <div className="kbd text-[var(--ink-3)] mt-1">{t.role}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── FINAL CTA ───────────── */}
      <section className="section bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <div className="bg-white border border-[var(--line-soft)] rounded-2xl px-8 md:px-14 py-14 md:py-20 text-center">
            <Reveal>
              <div className="overline overline-green">Join the Movement</div>
              <h2 className="font-serif-display text-4xl md:text-6xl mt-5 leading-tight text-[var(--ink)] font-semibold max-w-4xl mx-auto">
                The millet movement has a <span className="text-[var(--green)]">native touch.</span>
              </h2>
              <div className="mt-10 flex flex-wrap gap-3 justify-center">
                <Link to="/brands" data-testid="final-cta-brands" className="btn-primary">Explore Brands <ArrowRight size={14} /></Link>
                <Link to="/products" data-testid="final-cta-products" className="btn-outline">Shop Products</Link>
                <Link to="/contact" data-testid="final-cta-contact" className="btn-outline">Get in Touch</Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
